"use client";

import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  EllipsisVerticalIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { setMailMessage, setMailRoomsLoad } from "../../redux/mailSlice";
import moment from "moment";
import { getUser, setMailId } from "../../Localstorage/localstorage";
import SelectPatientMail from "../../Componenten/SelectPatientMail";
import { useCallback, useLayoutEffect, useMemo } from "react";
import { seenMessage } from "../../apiCalls/messageApi";
import { wa } from "./mailWaTheme";
import { avatarInitial, contactAvatarUrl } from "../../utils/mailContactAvatar";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const MailboxKlant = ({ setOpenModal, herLaden, onConversationOpen, mobileChatOpen }) => {
  const { mailRooms, mailMessage } = useSelector((state) => state.mail);
  const dispatch = useDispatch();
  const user = getUser();

  const updateMailMessage = useCallback(async (p_items, isClicked = true) => {
    setMailId(p_items);
    if (isClicked === true) {
      // WhatsApp-like: hide unread badge immediately when opening thread.
      dispatch(
        setMailRoomsLoad(
          (mailRooms || []).map((room) =>
            room?.id === p_items?.id ? { ...room, sum_status: 0 } : room
          )
        )
      );
      seenMessage(user, p_items, dispatch);
    }
    dispatch(setMailMessage(p_items));
    if (isClicked) onConversationOpen?.();
  }, [dispatch, mailRooms, onConversationOpen, user]);

  useLayoutEffect(() => {
    if (mailRooms?.length > 0) updateMailMessage(mailRooms[0], false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roomRows = useMemo(() => {
    if (!mailRooms?.length) return null;
    return mailRooms.map((message) => {
      const active = message.id === mailMessage?.id;
      const unreadCount = Number(
        message?.sum_status ??
          message?.unread_count ??
          message?.unread_messages_count ??
          0
      );
      const unread = unreadCount > 0;
      const label = message?.receiver || "Therapeut";
      const initial = avatarInitial(label);
      const rowAvatar = contactAvatarUrl(message, user?.id);
      return (
        <li key={message?.id} className="px-2 py-1.5">
          <button
            type="button"
            onClick={() => updateMailMessage(message, true)}
            className={classNames(
              "flex w-full gap-3 rounded-2xl border px-3 py-3 text-left transition-all duration-150",
              active
                ? "border-wa-accent/45 bg-wa-accent/10 shadow-[0_8px_24px_rgba(0,168,132,0.14)]"
                : "border-transparent",
              !active && "hover:border-wa-border hover:bg-wa-hover"
            )}
          >
            <div
              className={classNames(
                "relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-full border border-wa-border/70 text-lg font-medium",
                wa.panel2,
                wa.text
              )}
            >
              {rowAvatar ? (
                <img src={rowAvatar} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
              ) : (
                <span className="flex h-full w-full items-center justify-center">{initial}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <p className={classNames("truncate font-medium", wa.text)}>{label}</p>
                <time dateTime={message?.lastdate_updated} className={classNames("flex-shrink-0 text-xs", wa.muted)}>
                  {moment(message?.lastdate_updated).format("HH:mm")}
                </time>
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-2">
                <p className={classNames("truncate text-sm", unread ? wa.text : wa.muted)}>
                  {message?.subject || "—"}
                </p>
                {unread ? (
                  <span
                    className={classNames(
                      "inline-flex h-5 min-w-[1.25rem] flex-shrink-0 items-center justify-center rounded-full px-1 text-xs font-semibold",
                      active
                        ? "bg-wa-accentdeep text-white"
                        : wa.badge
                    )}
                  >
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                ) : null}
              </div>
            </div>
          </button>
        </li>
      );
    });
  }, [mailMessage?.id, mailRooms, updateMailMessage, user?.id]);

  return (
    <aside
      className={classNames(
        "flex h-full min-h-0 w-full flex-shrink-0 flex-col border-r md:max-w-[420px] md:w-[min(100%,400px)]",
        wa.border,
        wa.panel,
        mobileChatOpen ? "hidden md:flex" : "flex"
      )}
    >
      <header
        className={classNames(
          "flex flex-shrink-0 items-center justify-between gap-2 px-3 py-2.5",
          wa.panel2
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={classNames(
              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
              wa.badge,
              "text-sm font-semibold"
            )}
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <h2 className={classNames("truncate text-lg font-semibold leading-tight", wa.text)}>
              Berichten
            </h2>
            <p className={classNames("text-xs", wa.muted)}>
              {mailRooms?.length || 0} gesprekken
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={herLaden}
            title="Vernieuwen"
            className={classNames(
              "rounded-full p-2 transition-colors",
              wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
          >
            <ArrowPathIcon className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setOpenModal("insert")}
            title="Nieuw bericht"
            className={classNames(
              "rounded-full p-2 transition-colors",
              wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
          >
            <PlusIcon className="h-6 w-6" aria-hidden />
          </button>
          <button
            type="button"
            className={classNames(
              "rounded-full p-2 transition-colors",
              wa.muted,
              wa.hover,
              "hover:text-wa-text"
            )}
            aria-label="Meer"
          >
            <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </header>

      <div className={classNames("flex-shrink-0 border-b px-2 pb-2 pt-1", wa.border, wa.panel)}>
        <SelectPatientMail dispatch={dispatch} mailMessage={mailMessage} user={user} key="Mail" />
      </div>

      <nav aria-label="Gesprekken" className={classNames("min-h-0 flex-1 overflow-y-auto py-1", wa.panel)}>
        <ul role="list">
          {roomRows ? (
            roomRows
          ) : (
            <li className={classNames("px-6 py-12 text-center text-sm", wa.muted)}>
              Geen gesprekken. Start een nieuw bericht of pas je zoekopdracht aan.
            </li>
          )}
        </ul>
      </nav>
    </aside>
  );
};

export default MailboxKlant;
